import { X } from 'lucide-react'
import { useLanguage } from '@/contexts/language-context'
import { cn } from '@/lib/utils'

export interface ActiveFilterPillsProps {
  selectedWorkerTypes: string[]
  selectedJobFunctions: string[]
  selectedSkills: string[]
  selectedCertifications: string[]
  selectedProficiencyLevels: string[]
  selectedBranches: string[]
  selectedWorkerLocations: string[]
  selectedProcurementSources: string[]
  selectedFunctionalManagers: string[]
  selectedEngagements: string[]
  onWorkerTypesChange: (next: string[]) => void
  onJobFunctionsChange: (next: string[]) => void
  onSkillsChange: (next: string[]) => void
  onCertificationsChange: (next: string[]) => void
  onProficiencyLevelsChange: (next: string[]) => void
  onBranchesChange: (next: string[]) => void
  onWorkerLocationsChange: (next: string[]) => void
  onProcurementSourcesChange: (next: string[]) => void
  onFunctionalManagersChange: (next: string[]) => void
  onEngagementsChange: (next: string[]) => void
  onResetAll: () => void
  /** Optional code → display-name resolvers — same as the filter pane uses. The pill's
   *  underlying value stays the raw code; only its rendered label changes. */
  resolveBranchLabel?: (code: string) => string
  resolveWorkerLocationLabel?: (code: string) => string
  resolveProcurementSourceLabel?: (code: string) => string
  resolveFunctionalManagerLabel?: (idStr: string) => string
  resolveEngagementLabel?: (id: string) => string
  resolveSkillLabel?: (idStr: string) => string
  resolveCertificationLabel?: (idStr: string) => string
  resolveProficiencyLevelLabel?: (idStr: string) => string
}

interface PillSpec {
  category: string
  value: string
  onRemove: () => void
}

/**
 * Inline pill row reflecting every filter currently active in the slide-in pane. Lets users
 * see and individually remove filters without re-opening the pane. Renders nothing when no
 * filters are active.
 */
export function ActiveFilterPills(props: ActiveFilterPillsProps) {
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.filterPane
  const tBar = dictionary?.resourceAvailability?.filterBar

  const pills: PillSpec[] = []
  const remove = <T extends string>(arr: T[], setter: (next: T[]) => void, value: T) => () =>
    setter(arr.filter(v => v !== value))
  const resolved = (label: string | undefined, code: string) =>
    label && label.trim().length > 0 ? label : code

  for (const v of props.selectedWorkerTypes) {
    pills.push({
      category: t?.workerTypeSection || (tBar?.workerType || 'Worker Type'),
      value: v,
      onRemove: remove(props.selectedWorkerTypes, props.onWorkerTypesChange, v),
    })
  }
  for (const v of props.selectedJobFunctions) {
    pills.push({
      category: t?.jobFunctionSection || (tBar?.jobFunction || 'Job Function'),
      value: v,
      onRemove: remove(props.selectedJobFunctions, props.onJobFunctionsChange, v),
    })
  }
  for (const v of props.selectedSkills) {
    pills.push({
      category: t?.skillsSection || (tBar?.skills || 'Skills'),
      value: resolved(props.resolveSkillLabel?.(v), v),
      onRemove: remove(props.selectedSkills, props.onSkillsChange, v),
    })
  }
  for (const v of props.selectedCertifications) {
    pills.push({
      category: t?.certificationsSection || (tBar?.certifications || 'Certifications'),
      value: resolved(props.resolveCertificationLabel?.(v), v),
      onRemove: remove(props.selectedCertifications, props.onCertificationsChange, v),
    })
  }
  for (const v of props.selectedProficiencyLevels) {
    pills.push({
      category: t?.proficiencyLevelsSection || (tBar?.proficiencyLevels || 'Proficiency Level'),
      value: resolved(props.resolveProficiencyLevelLabel?.(v), v),
      onRemove: remove(props.selectedProficiencyLevels, props.onProficiencyLevelsChange, v),
    })
  }
  for (const v of props.selectedBranches) {
    pills.push({
      category: t?.branchSection || 'Branch',
      value: resolved(props.resolveBranchLabel?.(v), v),
      onRemove: remove(props.selectedBranches, props.onBranchesChange, v),
    })
  }
  for (const v of props.selectedWorkerLocations) {
    pills.push({
      category: t?.workerLocationSection || 'Worker location',
      value: resolved(props.resolveWorkerLocationLabel?.(v), v),
      onRemove: remove(props.selectedWorkerLocations, props.onWorkerLocationsChange, v),
    })
  }
  for (const v of props.selectedProcurementSources) {
    pills.push({
      category: t?.procurementSourceSection || 'Procurement source',
      value: resolved(props.resolveProcurementSourceLabel?.(v), v),
      onRemove: remove(props.selectedProcurementSources, props.onProcurementSourcesChange, v),
    })
  }
  for (const v of props.selectedFunctionalManagers) {
    pills.push({
      category: t?.functionalManagerSection || 'Functional manager',
      value: resolved(props.resolveFunctionalManagerLabel?.(v), v),
      onRemove: remove(props.selectedFunctionalManagers, props.onFunctionalManagersChange, v),
    })
  }
  for (const v of props.selectedEngagements) {
    pills.push({
      category: t?.engagementSection || 'Engagement',
      value: resolved(props.resolveEngagementLabel?.(v), v),
      onRemove: remove(props.selectedEngagements, props.onEngagementsChange, v),
    })
  }

  if (pills.length === 0) return null

  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-3">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mr-1">
        {t?.activeFiltersLabel || 'Active filters'}
      </span>
      {pills.map((p, i) => {
        const removeAria = (t?.removePillAria || 'Remove {label} filter')
          .replace('{label}', `${p.category}: ${p.value}`)
        return (
          <span
            key={`${p.category}-${p.value}-${i}`}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border bg-blue-50 border-blue-200 text-blue-800',
              'pl-2 pr-1 py-0.5 text-[11px]'
            )}
          >
            <span className="text-blue-600 font-medium">{p.category}:</span>
            <span className="truncate max-w-[12rem]">{p.value}</span>
            <button
              type="button"
              onClick={p.onRemove}
              aria-label={removeAria}
              className="p-0.5 rounded-full hover:bg-blue-200/60 text-blue-700"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        )
      })}
      <button
        type="button"
        onClick={props.onResetAll}
        className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline ml-1"
      >
        {t?.clearAllPills || 'Clear all'}
      </button>
    </div>
  )
}
